import React, { useState, useEffect } from "react";
import { Alert, Table, Button, Modal, Form, Input, Popconfirm, Space, Typography, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Axios from "axios";
import moment from "moment";
import { PricingAPI } from "../../../controller/PricingAPI";

/**
 * 要填進 SurveyCake 的 webhook 網址。
 *
 * 由 Axios 的 baseURL 串出而非後端的 APP_URL —— baseURL 就是這個畫面實際在打的
 * API 位址 (見 src/index.js)，所以正式、測試、本機三種建置各自都會得到對的網址，
 * 而且 .env 的 APP_URL 寫錯時不會給出騙人的答案。
 *
 * 在呼叫時取值而不是在模組載入時 —— baseURL 是 src/index.js 啟動時才設定的，
 * 模組載入的先後順序會決定我們讀到的是網址還是 undefined。
 */
function resolveWebhookUrl() {
  return `${(Axios.defaults.baseURL || "/api").replace(/\/+$/, "")}/webhooks/surveycake`;
}

/**
 * SurveyCake 問卷金鑰的管理頁。
 *
 * 金鑰是「每份問卷各一組」，因此獨立於通用設定頁之外 —— 那一頁是平面的鍵值表，
 * 沒有「重複列」的概念，硬塞會讓它長出領域知識。
 *
 * svid 與問卷名稱由系統從第一筆成功解密的回覆學來，不要求人填 ——
 * svid 不在問卷網址裡，要求先填會變成「要看第一筆回覆才知道，而第一筆正好
 * 會被丟掉」的雞生蛋問題。
 */
export default function SurveyCakeSurveyPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    PricingAPI.surveycakeSurveys
      .index()
      .then((res) => setList(res.data || []))
      .catch(() => message.error("載入問卷清單失敗"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    // 金鑰欄位一律留空 —— 後端不回傳本體，空值代表「不變更」
    form.setFieldsValue({ label: row.label, hash_key: "", iv_key: "" });
    setModalOpen(true);
  };

  const submit = () => {
    form
      .validateFields()
      .then((v) => {
        const req = editing
          ? PricingAPI.surveycakeSurveys.update(editing.id, v)
          : PricingAPI.surveycakeSurveys.create(v);
        req
          .then(() => {
            message.success("已儲存");
            setModalOpen(false);
            load();
          })
          .catch((e) => {
            const errors = e && e.response && e.response.data && e.response.data.errors;
            message.error(errors ? Object.values(errors)[0][0] : "儲存失敗");
          });
      },
      // 前端驗證失敗 (例如兩把金鑰只填一把) 時 antd 已經在欄位上顯示錯誤訊息，
      // 這裡不需要再做什麼；只是不能讓 validateFields() 的 rejection 變成
      // 未處理的 promise rejection。
      //
      // 用 then 的第二個參數而不是接一個 .catch()：後者會連成功處理函式裡拋出的
      // 例外一起吞掉，按鈕會變成毫無反應且主控台不留任何線索。
      () => {});
  };

  const remove = (row) => {
    PricingAPI.surveycakeSurveys
      .remove(row.id)
      .then(() => {
        message.success("已刪除");
        load();
      })
      .catch(() => message.error("刪除失敗"));
  };

  const secondary = (text) => <Typography.Text type="secondary">{text}</Typography.Text>;

  // 每一欄都指定寬度：末四碼提示與時間戳被擠成兩行時，整張表的列高會參差不齊
  const columns = [
    {
      title: "名稱",
      dataIndex: "label",
      key: "label",
      width: 200,
      render: (v) => v || secondary("(未命名)"),
    },
    {
      title: "svid",
      dataIndex: "svid",
      key: "svid",
      width: 160,
      render: (v) => v || secondary("—"),
    },
    {
      title: "Hash Key",
      dataIndex: "hash_key_hint",
      key: "hash_key_hint",
      width: 120,
      // hash_key 在後端是 NOT NULL，hint 為 null 唯一的可能是密文解不開
      // (APP_KEY 換過或資料毀損)——這不是「沒填」而是「壞了」，
      // 渲染成空白會把這個狀態藏起來，必須用醒目色標出來讓人進來修
      render: (v) => v || <Typography.Text type="danger">(金鑰無法解讀)</Typography.Text>,
    },
    {
      title: "IV Key",
      dataIndex: "iv_key_hint",
      key: "iv_key_hint",
      width: 120,
      render: (v) => v || <Typography.Text type="danger">(金鑰無法解讀)</Typography.Text>,
    },
    {
      title: "最後收件",
      dataIndex: "last_received_at",
      key: "last_received_at",
      width: 170,
      render: (v) => (v ? moment(v).format("YYYY-MM-DD HH:mm") : secondary("尚未收到")),
    },
    {
      title: "操作",
      key: "action",
      width: 140,
      render: (t, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>編輯</Button>
          {/* antd 4.19.5 的 Popconfirm 沒有 description prop (那是 5.1 才加的)，
              補充說明必須包在 title 裡 */}
          <Popconfirm
            title={
              <div style={{ maxWidth: 260 }}>
                刪除「{row.label || "未命名問卷"}」的金鑰？
                <br />
                已收進來的回饋不會被刪除，但這份問卷之後的回覆會解不開。
              </div>
            }
            onConfirm={() => remove(row)}
            okText="刪除"
            okButtonProps={{ danger: true }}
            cancelText="取消"
          >
            <Button size="small" danger>刪除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Typography.Title level={3} style={{ marginBottom: 8 }}>SurveyCake 問卷</Typography.Title>
      <Typography.Paragraph type="secondary">
        金鑰是「每份問卷各一組」，在該問卷的「通知 → Webhook」頁面取得，不是帳號共用。
        多份問卷共用同一條 webhook 網址，按「新增問卷」就會顯示要填的網址。
        svid 與名稱不必填 —— 收到第一筆回覆後會自動補上。
        若 Hash Key 或 IV Key 顯示「(金鑰無法解讀)」，代表密文已經解不開，
        請點選「編輯」重新填入該問卷的金鑰。
      </Typography.Paragraph>
      {/* 操作按鈕獨立一列，緊鄰它作用的表格 */}
      <Space style={{ marginBottom: 12 }} align="center">
        <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>新增問卷</Button>
      </Space>
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        size="small"
        pagination={false}
        scroll={{ x: 910 }}
      />

      <Modal
        title={editing ? `編輯：${editing.label || "未命名問卷"}` : "新增問卷"}
        visible={modalOpen}
        onOk={submit}
        onCancel={() => setModalOpen(false)}
        okText="儲存"
        cancelText="取消"
        destroyOnClose
      >
        {/* 只在新增時提示：這是「去 SurveyCake 那邊設定」的那一步，編輯既有問卷
            (換金鑰) 時 webhook 早就設好了，再放一次只是雜訊 */}
        {!editing && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="先到 SurveyCake 設定 webhook"
            description={
              <div>
                在該問卷的「通知 → Webhook」頁面，把通知網址填成：
                <div style={{ margin: "6px 0" }}>
                  <Typography.Text code copyable={{ text: resolveWebhookUrl() }}>
                    {resolveWebhookUrl()}
                  </Typography.Text>
                </div>
                多份問卷共用這一條網址，系統會自動辨認回覆屬於哪一份。同一個頁面也會給你下面要填的兩把金鑰。
              </div>
            }
          />
        )}
        <Form form={form} layout="vertical">
          <Form.Item
            name="label"
            label="名稱"
            extra="留空的話，收到第一筆回覆時會自動填入問卷標題"
            rules={[{ max: 100, message: "名稱最多 100 個字元" }]}
          >
            <Input placeholder="例如 住宿滿意度問卷" />
          </Form.Item>
          {/* SurveyCake 的兩把金鑰是一起換的，只填一把會產生錯配、永遠解不開
              任何回覆 (後端 required_with 也擋這個)。編輯模式下用函式形式的
              rules 搭配 getFieldValue 做跨欄互相要求，dependencies 讓另一欄
              變動時這裡會重新驗證 —— 這是 rc-field-form (antd Form 底層) 從
              4.0 就支援的寫法，已在本專案實際安裝的 4.19.5 (node_modules/antd
              /lib/form/FormItem.d.ts、node_modules/rc-field-form/lib/
              interface.d.ts 的 RuleRender / dependencies 型別定義) 核對過。 */}
          <Form.Item
            name="hash_key"
            label="Hash Key"
            dependencies={editing ? ["iv_key"] : undefined}
            rules={
              editing
                ? [
                    { len: 16, message: "必須剛好 16 個字元" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (getFieldValue("iv_key") && !value) {
                          return Promise.reject(new Error("兩把金鑰必須一起更換"));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]
                : [{ required: true, len: 16, message: "必須剛好 16 個字元" }]
            }
            extra={editing ? "留空表示不變更；若要更新，兩把金鑰必須一起填" : "AES-128 要求長度必須剛好 16 個字元"}
          >
            <Input placeholder={editing ? "留空表示不變更" : ""} />
          </Form.Item>
          <Form.Item
            name="iv_key"
            label="IV Key"
            dependencies={editing ? ["hash_key"] : undefined}
            rules={
              editing
                ? [
                    { len: 16, message: "必須剛好 16 個字元" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (getFieldValue("hash_key") && !value) {
                          return Promise.reject(new Error("兩把金鑰必須一起更換"));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]
                : [{ required: true, len: 16, message: "必須剛好 16 個字元" }]
            }
            extra={editing ? "留空表示不變更；若要更新，兩把金鑰必須一起填" : "AES-128 要求長度必須剛好 16 個字元"}
          >
            <Input placeholder={editing ? "留空表示不變更" : ""} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
